import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import {Lock, ArrowUpRight, ArrowDownLeft, ChevronLeft, ChevronRight} from "lucide-react";
import { ProgressDataItem, useGetProgressData } from "@/services/ProjectsService";
import { addMonthsMmYy, buildMonthRange, getDefaultSixMonthRangeMmYy, mmYyToDate, monthsDiffInclusiveMmYy, normalizeToMmYy, shiftRangeMmYy } from "@/lib/progressRange";
import { MonthYearPicker } from "@/components/projects/components/MonthYearPicker";
import {
    Line,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Area,
    AreaChart,
    ReferenceLine,
    Tooltip,
} from "recharts";

interface ProgressTrackerProps {
    /**
     * If provided, the component will not fetch.
     */
    data?: ProgressDataItem[];
    /**
     * If `data` is not provided, the component will fetch using `companyId` and `token`.
     */
    companyId?: number;
    token?: string | null;
    /**
     * Lower bound for navigation (inclusive). When shifting left would make startDate < minStartDate,
     * the left arrow is disabled.
     * Format: mm-yy
     */
    minStartDate?: string;
    /**
     * Newest available report month (mm-yy). Used as the base "current" month for bounds.
     * If not provided, falls back to the actual current month.
     */
    maxReportDate?: string;
    trending?: string;
    isFree?: boolean;
    onUnlock?: () => void;
}

/** The tracker is always chromeless: its only mount is the score card's Trend
 *  tab, which already supplies the surface and padding. (AITS-491 kept a
 *  card-wrapped branch for a standalone mount that never arrived; AITS-501
 *  removed it along with the `embedded` prop that selected between them.) */
function ProgressShell({ children }: { children: React.ReactNode }) {
    return <div className="flex h-full flex-col">{children}</div>;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({ data, companyId, token, minStartDate, maxReportDate, trending, isFree, onUnlock }) => {
    const MIN_RANGE_MONTHS = 3;
    const MIN_RANGE_OFFSET = MIN_RANGE_MONTHS - 1; // inclusive span => end must be at least start + 2 months

    const now = useMemo(() => new Date(), []);
    const actualCurrentMonthMmYy = useMemo(() => getDefaultSixMonthRangeMmYy(now).endMmYy, [now]);
    const minStartMmYy = useMemo(() => normalizeToMmYy(minStartDate) ?? null, [minStartDate]);
    const latestReportMmYy = useMemo(
        () => normalizeToMmYy(maxReportDate) ?? null,
        [maxReportDate],
    );

    const isLockedForecastMode = useMemo(() => {
        if (!minStartMmYy || !latestReportMmYy) return false;
        const span = monthsDiffInclusiveMmYy(minStartMmYy, latestReportMmYy);
        return Number.isFinite(span) && span < 6;
    }, [latestReportMmYy, minStartMmYy]);

    const maxSelectableEndMmYy = useMemo(
        () => latestReportMmYy ?? actualCurrentMonthMmYy,
        [actualCurrentMonthMmYy, latestReportMmYy],
    );
    const maxStartByMaxEndMmYy = useMemo(
        () => addMonthsMmYy(maxSelectableEndMmYy, -MIN_RANGE_OFFSET),
        [MIN_RANGE_OFFSET, maxSelectableEndMmYy],
    );

    const computedDefaultRange = useMemo(() => {
        if (isLockedForecastMode && minStartMmYy) {
            // Fixed 6-month window starting at the oldest report month; enables forecast line for missing months.
            return { startMmYy: minStartMmYy, endMmYy: addMonthsMmYy(minStartMmYy, 5) };
        }

        const baseEnd = maxSelectableEndMmYy;
        const baseStart = addMonthsMmYy(baseEnd, -5);
        return { startMmYy: baseStart, endMmYy: baseEnd };
    }, [isLockedForecastMode, maxSelectableEndMmYy, minStartMmYy]);

    const [range, setRange] = useState<{ startMmYy: string; endMmYy: string }>(() => ({
        startMmYy: computedDefaultRange.startMmYy,
        endMmYy: computedDefaultRange.endMmYy,
    }));

    const prevLockedRef = useRef<boolean>(isLockedForecastMode);
    useEffect(() => {
        const prevLocked = prevLockedRef.current;

        // When switching modes, always reset to the computed default.
        if (prevLocked !== isLockedForecastMode) {
            prevLockedRef.current = isLockedForecastMode;
            setRange({
                startMmYy: computedDefaultRange.startMmYy,
                endMmYy: computedDefaultRange.endMmYy,
            });
            return;
        }

        // In locked mode, keep the range synced to the computed default (UI is disabled).
        if (isLockedForecastMode) {
            if (
                range.startMmYy !== computedDefaultRange.startMmYy ||
                range.endMmYy !== computedDefaultRange.endMmYy
            ) {
                setRange({
                    startMmYy: computedDefaultRange.startMmYy,
                    endMmYy: computedDefaultRange.endMmYy,
                });
            }
        }
    }, [
        computedDefaultRange.endMmYy,
        computedDefaultRange.startMmYy,
        isLockedForecastMode,
        range.endMmYy,
        range.startMmYy,
    ]);

    const isOnDefaultRange = useMemo(() => {
        return (
            range.startMmYy === computedDefaultRange.startMmYy &&
            range.endMmYy === computedDefaultRange.endMmYy
        );
    }, [computedDefaultRange.endMmYy, computedDefaultRange.startMmYy, range.endMmYy, range.startMmYy]);

    // Enforce minimum range and global bounds (unlocked mode only).
    useEffect(() => {
        if (isLockedForecastMode) return;
        setRange((prev) => {
            let start = prev.startMmYy;
            let end = prev.endMmYy;

            if (minStartMmYy) {
                const startTs = mmYyToDate(start).getTime();
                const minTs = mmYyToDate(minStartMmYy).getTime();
                if (Number.isFinite(startTs) && Number.isFinite(minTs) && startTs < minTs) start = minStartMmYy;
            }

            // start cannot be later than maxEnd - (minRange-1)
            {
                const startTs = mmYyToDate(start).getTime();
                const maxStartTs = mmYyToDate(maxStartByMaxEndMmYy).getTime();
                if (Number.isFinite(startTs) && Number.isFinite(maxStartTs) && startTs > maxStartTs) start = maxStartByMaxEndMmYy;
            }

            // end cannot exceed maxSelectableEnd
            {
                const endTs = mmYyToDate(end).getTime();
                const maxEndTs = mmYyToDate(maxSelectableEndMmYy).getTime();
                if (Number.isFinite(endTs) && Number.isFinite(maxEndTs) && endTs > maxEndTs) end = maxSelectableEndMmYy;
            }

            // Ensure minimum inclusive span
            const minEndForStart = addMonthsMmYy(start, MIN_RANGE_OFFSET);
            {
                const endTs = mmYyToDate(end).getTime();
                const minEndTs = mmYyToDate(minEndForStart).getTime();
                if (Number.isFinite(endTs) && Number.isFinite(minEndTs) && endTs < minEndTs) {
                    end = minEndForStart;
                }
            }

            // If end got pushed beyond maxEnd, clamp end and pull start back to keep minimum range.
            {
                const endTs = mmYyToDate(end).getTime();
                const maxEndTs = mmYyToDate(maxSelectableEndMmYy).getTime();
                if (Number.isFinite(endTs) && Number.isFinite(maxEndTs) && endTs > maxEndTs) {
                    end = maxSelectableEndMmYy;
                    start = addMonthsMmYy(end, -MIN_RANGE_OFFSET);
                    if (minStartMmYy) {
                        const startTs = mmYyToDate(start).getTime();
                        const minTs = mmYyToDate(minStartMmYy).getTime();
                        if (Number.isFinite(startTs) && Number.isFinite(minTs) && startTs < minTs) {
                            start = minStartMmYy;
                            end = addMonthsMmYy(start, MIN_RANGE_OFFSET);
                        }
                    }
                }
            }

            if (start === prev.startMmYy && end === prev.endMmYy) return prev;
            return { startMmYy: start, endMmYy: end };
        });
    }, [MIN_RANGE_OFFSET, isLockedForecastMode, maxSelectableEndMmYy, maxStartByMaxEndMmYy, minStartMmYy]);

    const canMoveForward = useMemo(() => {
        if (isLockedForecastMode) return false;
        const nextEnd = addMonthsMmYy(range.endMmYy, +1);
        const nextEndTs = mmYyToDate(nextEnd).getTime();
        const maxEndTs = mmYyToDate(maxSelectableEndMmYy).getTime();
        if (!Number.isFinite(nextEndTs) || !Number.isFinite(maxEndTs)) return false;
        return nextEndTs <= maxEndTs;
    }, [isLockedForecastMode, maxSelectableEndMmYy, range.endMmYy]);

    const canMoveBackward = useMemo(() => {
        if (isLockedForecastMode) return false;
        if (!minStartMmYy) return true;
        const nextStart = addMonthsMmYy(range.startMmYy, -1);
        const nextStartTs = mmYyToDate(nextStart).getTime();
        const minStartTs = mmYyToDate(minStartMmYy).getTime();
        if (!Number.isFinite(nextStartTs) || !Number.isFinite(minStartTs)) return false;
        return nextStartTs >= minStartTs;
    }, [isLockedForecastMode, minStartMmYy, range.startMmYy]);

    const shouldFetch = !isFree && !data && !!companyId && !!token;
    const queryEndMmYy = isLockedForecastMode && maxSelectableEndMmYy ? maxSelectableEndMmYy : range.endMmYy;
    const { data: fetchedData } = useGetProgressData({
        token: token ?? null,
        companyId: companyId ?? null,
        startDate: range.startMmYy,
        endDate: queryEndMmYy,
        enabled: shouldFetch,
    });

    const effectiveData = data ?? fetchedData;

    const startMmYy = range.startMmYy;
    const endMmYy = range.endMmYy;

    const months = buildMonthRange(startMmYy, endMmYy);
    const xAxisInterval = useMemo(() => {
        // Keep the axis readable by capping visible labels; Recharts will render every (interval + 1)-th tick.
        const MAX_VISIBLE_TICKS = 8;
        if (months.length <= MAX_VISIBLE_TICKS) return 0;
        return Math.ceil(months.length / MAX_VISIBLE_TICKS) - 1;
    }, [months.length]);
    if (isFree) {
        const mockData = [
            { name: "Sep", value: 62 },
            { name: "Oct", value: 68 },
            { name: "Nov", value: 66 },
            { name: "Dec", value: 72 },
            { name: "Jan", value: 69 },
            { name: "Feb", value: 78 },
        ];

        return (
            <ProgressShell>
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-sm md:text-md font-semibold text-vb-text-3">Progress score</h3>
                        <span className="text-xs md:text-sm text-vb-text-3">
                            {months.length > 0 ? `${months[0].fullLabel} - ${months[months.length - 1].fullLabel}` : "—"}
                        </span>
                    </div>
                    <div className="relative h-60 w-full">
                        <div style={{ filter: 'blur(6px)', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={mockData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="colorGradientBlueMock" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="-37.65%" stopColor="rgba(var(--vb-brand-rgb), 0.28)" />
                                            <stop offset="100.62%" stopColor="rgba(var(--vb-brand-rgb), 0)" />
                                        </linearGradient>
                                    </defs>
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 12, fill: "var(--vb-text-3)" }}
                                        type="category"
                                    />
                                    <YAxis
                                        domain={[0, 100]}
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 12, fill: "var(--vb-text-3)" }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="value"
                                        stroke="none"
                                        fill="url(#colorGradientBlueMock)"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="var(--vb-brand)"
                                        strokeWidth={2}
                                        dot={{ fill: "var(--vb-brand)", strokeWidth: 0, r: 4 }}
                                        connectNulls={false}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <Button variant="gold" onClick={onUnlock} className="flex items-center">
                                <Lock className="mr-2 h-4 w-4" />
                                Subscribe to unlock progress insights
                            </Button>
                        </div>
                    </div>
            </ProgressShell>
        );
    }

    const dataMap = effectiveData?.reduce((acc, item) => {
        const key = normalizeToMmYy(item.date);
        if (!key) return acc;

        const raw = typeof item.score === "string" ? parseFloat(item.score) : item.score;
        if (!Number.isFinite(raw)) return acc;
        // Backend may return 0..1 or 0..100
        const val = raw <= 1 ? raw * 100 : raw;
        acc[key] = Math.round(val);
        return acc;
    }, {} as Record<string, number>) || {};

    const base = months.map((m) => ({
        key: m.key,
        name: m.tickLabel,
        fullLabel: m.fullLabel,
        value: dataMap[m.key] ?? null,
        gap: null as number | null,
        forecast: null as number | null,
    }));

    // Find indices where we have real data
    const dataIndexes = base
        .map((d, i) => (d.value !== null ? i : -1))
        .filter((i) => i !== -1);

    const lastActualIndex =
        dataIndexes.length > 0 ? dataIndexes[dataIndexes.length - 1] : -1;

    const withGaps = [...base];

    // --- GREY "GAP" LINES ---
    // Only connect when there is a gap (>1 month)
    if (dataIndexes.length > 1) {
        for (let i = 0; i < dataIndexes.length - 1; i++) {
            const from = dataIndexes[i];
            const to = dataIndexes[i + 1];
            const gap = to - from;

            if (gap > 1) {
                const fromVal = base[from].value!;
                const toVal = base[to].value!;
                for (let j = 0; j <= gap; j++) {
                    const t = j / gap;
                    const wave = Math.sin(t * Math.PI) * 3;
                    const interp = fromVal + (toVal - fromVal) * t + wave;
                    withGaps[from + j].gap = interp;
                }
            }
        }
    }

    // --- FORECAST (dashed) ---
    if (isLockedForecastMode && lastActualIndex !== -1) {
        const startVal = base[lastActualIndex].value!;
        withGaps[lastActualIndex].forecast = startVal;

        for (let i = lastActualIndex + 1; i < withGaps.length; i++) {
            const t = (i - lastActualIndex) / (withGaps.length - lastActualIndex);

            const trend = t * 10;
            const waveAmplitude = 5;
            const waveFrequency = 3;

            const wave = Math.sin(t * Math.PI * waveFrequency * 2) * waveAmplitude;

            withGaps[i].forecast = startVal + trend + wave;
        }
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active) return null;

        const pointKey = payload?.[0]?.payload?.key as string | undefined;
        const pointIndex = pointKey ? withGaps.findIndex((p) => p.key === pointKey) : -1;
        const point = pointIndex >= 0 ? withGaps[pointIndex] : null;
        if (!point) return null;

        const hasValue = point.value !== null;
        const isForecast = isLockedForecastMode && pointIndex > lastActualIndex;

        if (isForecast) return null;

        if (!hasValue) {
            return (
                <div className="rounded-lg border border-vb-line bg-vb-surface px-3 py-2 shadow-vb-sm">
                    <p className="text-sm font-medium text-vb-text">{`${point.fullLabel}: No data available`}</p>
                </div>
            );
        }

        // Tooltip for actual months - round to avoid floating point precision issues
        const roundedValue = Math.round(point.value);
        return (
            <div className="rounded-lg border border-vb-line bg-vb-surface px-3 py-2 shadow-vb-sm">
                <p className="text-sm font-medium text-vb-text">{`${point.fullLabel}: ${roundedValue}%`}</p>
            </div>
        );
    };


    return (
        <ProgressShell>
                <div className="flex flex-col gap-3 mb-4">
                    {/* The reset-to-default control lives in the toolbar row
                        below; the phone-only duplicate that used to sit here was
                        gated on `!embedded` and so never rendered. */}
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm md:text-md font-semibold text-vb-text-3">Progress score</h3>
                    </div>
                    <div className="flex items-center w-full justify-end gap-2">
                        {!isLockedForecastMode && !isOnDefaultRange ? (
                            <button
                                type="button"
                                className="inline-flex border rounded-lg border-vb-line-strong px-2.5 py-1.5 text-xs md:text-sm text-vb-text-2 items-center justify-center hover:bg-vb-bg-subtle disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() =>
                                    setRange({
                                        startMmYy: computedDefaultRange.startMmYy,
                                        endMmYy: computedDefaultRange.endMmYy,
                                    })
                                }
                                aria-label="Reset to default range"
                            >
                                This Month
                            </button>
                        ) : null}
                        <div className="border border-vb-line flex justify-between items-center rounded-xl p-0.5 gap-2 sm:gap-4 w-full sm:w-auto">
                        <button
                            type="button"
                            className="border rounded-lg border-vb-line-strong flex items-center justify-center hover:bg-vb-bg-subtle disabled:opacity-50 disabled:cursor-not-allowed h-10 w-10 sm:h-auto sm:w-auto"
                            onClick={() => {
                                if (!canMoveBackward) return;
                                setRange((prev) => shiftRangeMmYy(prev, -1));
                            }}
                            disabled={!canMoveBackward}
                            aria-label="Previous month"
                        >
                            <ChevronLeft />
                        </button>
                            <div className="text-xs md:text-sm text-vb-text-3 flex items-center gap-2 flex-1 justify-center">
                                <MonthYearPicker
                                    value={range.startMmYy}
                                    min={minStartMmYy ?? undefined}
                                    max={maxStartByMaxEndMmYy}
                                    disabled={isLockedForecastMode}
                                    onChange={(next) => {
                                        const nextNorm = normalizeToMmYy(next) ?? next;
                                        setRange((prev) => {
                                            let start = nextNorm;
                                            let end = prev.endMmYy;

                                            // clamp start to minStart
                                            if (minStartMmYy) {
                                                const startTs = mmYyToDate(start).getTime();
                                                const minTs = mmYyToDate(minStartMmYy).getTime();
                                                if (Number.isFinite(startTs) && Number.isFinite(minTs) && startTs < minTs) start = minStartMmYy;
                                            }
                                            // clamp start to maxStartByMaxEnd
                                            {
                                                const startTs = mmYyToDate(start).getTime();
                                                const maxStartTs = mmYyToDate(maxStartByMaxEndMmYy).getTime();
                                                if (Number.isFinite(startTs) && Number.isFinite(maxStartTs) && startTs > maxStartTs) start = maxStartByMaxEndMmYy;
                                            }

                                            // ensure minimum range
                                            const minEndForStart = addMonthsMmYy(start, MIN_RANGE_OFFSET);
                                            const endTs = mmYyToDate(end).getTime();
                                            const minEndTs = mmYyToDate(minEndForStart).getTime();
                                            if (Number.isFinite(endTs) && Number.isFinite(minEndTs) && endTs < minEndTs) {
                                                end = minEndForStart;
                                            }
                                            // clamp end to maxEnd
                                            {
                                                const endTs2 = mmYyToDate(end).getTime();
                                                const maxEndTs = mmYyToDate(maxSelectableEndMmYy).getTime();
                                                if (Number.isFinite(endTs2) && Number.isFinite(maxEndTs) && endTs2 > maxEndTs) {
                                                    end = maxSelectableEndMmYy;
                                                }
                                            }
                                            return { startMmYy: start, endMmYy: end };
                                        });
                                    }}
                                />
                                <span className="text-vb-text-3">-</span>
                                <MonthYearPicker
                                    value={range.endMmYy}
                                    min={addMonthsMmYy(range.startMmYy, MIN_RANGE_OFFSET)}
                                    max={maxSelectableEndMmYy}
                                    disabled={isLockedForecastMode}
                                    onChange={(next) => {
                                        const nextNorm = normalizeToMmYy(next) ?? next;
                                        setRange((prev) => {
                                            let end = nextNorm;
                                            let start = prev.startMmYy;

                                            // clamp end to maxEnd
                                            {
                                                const endTs = mmYyToDate(end).getTime();
                                                const maxEndTs = mmYyToDate(maxSelectableEndMmYy).getTime();
                                                if (Number.isFinite(endTs) && Number.isFinite(maxEndTs) && endTs > maxEndTs) end = maxSelectableEndMmYy;
                                            }

                                            // ensure minimum range by pulling start back if needed
                                            const maxStartForEnd = addMonthsMmYy(end, -MIN_RANGE_OFFSET);
                                            const startTs = mmYyToDate(start).getTime();
                                            const maxStartTs = mmYyToDate(maxStartForEnd).getTime();
                                            if (Number.isFinite(startTs) && Number.isFinite(maxStartTs) && startTs > maxStartTs) {
                                                start = maxStartForEnd;
                                            }

                                            // clamp start to minStart
                                            if (minStartMmYy) {
                                                const startTs2 = mmYyToDate(start).getTime();
                                                const minTs = mmYyToDate(minStartMmYy).getTime();
                                                if (Number.isFinite(startTs2) && Number.isFinite(minTs) && startTs2 < minTs) {
                                                    start = minStartMmYy;
                                                    end = addMonthsMmYy(start, MIN_RANGE_OFFSET);
                                                }
                                            }

                                            return { startMmYy: start, endMmYy: end };
                                        });
                                    }}
                                />
                            </div>
                        <button
                            type="button"
                            className="border rounded-lg border-vb-line-strong flex items-center justify-center hover:bg-vb-bg-subtle disabled:opacity-50 disabled:cursor-not-allowed h-10 w-10 sm:h-auto sm:w-auto"
                            onClick={() => setRange((prev) => shiftRangeMmYy(prev, +1))}
                            disabled={!canMoveForward}
                            aria-label="Next month"
                        >
                            <ChevronRight />
                        </button>
                        </div>
                    </div>
                </div>

                <div className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={withGaps} margin={{ top: 5, right: 12, left: 0, bottom: 16 }}>
                            <defs>
                                {/* Blue fill under actual line */}
                                <linearGradient id="colorGradientBlue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="-37.65%" stopColor="rgba(var(--vb-brand-rgb), 0.28)" />
                                    <stop offset="100.62%" stopColor="rgba(var(--vb-brand-rgb), 0)" />
                                </linearGradient>

                                {/* Grey fill under gap line (token colour + opacity stops
                                    so no raw rgba is needed) */}
                                <linearGradient id="colorGradientGrey" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="-37.65%" stopColor="var(--vb-line-strong)" stopOpacity={0.35} />
                                    <stop offset="100.62%" stopColor="var(--vb-line-strong)" stopOpacity={0} />
                                </linearGradient>
                            </defs>

                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "var(--vb-text-3)" }}
                                type="category"
                                interval={xAxisInterval}
                                minTickGap={16}
                                tickMargin={8}
                                padding={{ left: 12, right: 12 }}
                            />
                            <YAxis
                                domain={[0, 100]}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "var(--vb-text-3)" }}
                                ticks={[0, 25, 50, 75, 100]}
                                width={32}
                            />

                            {[0, 25, 50, 75, 100].map((y) => (
                                <ReferenceLine key={y} y={y} stroke="var(--vb-line)" strokeWidth={1} />
                            ))}

                            <Tooltip content={<CustomTooltip />} />

                            <Area
                                type="monotone"
                                dataKey="gap"
                                stroke="none"
                                fill="url(#colorGradientGrey)"
                                connectNulls={true}
                            />
                            <Line
                                type="monotone"
                                dataKey="gap"
                                stroke="var(--vb-line-strong)"
                                strokeWidth={1.5}
                                opacity={0.9}
                                dot={false}
                                connectNulls={true}
                            />

                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="none"
                                fill="url(#colorGradientBlue)"
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="var(--vb-brand)"
                                strokeWidth={2}
                                dot={{ fill: "var(--vb-brand)", strokeWidth: 0, r: 4 }}
                                connectNulls={false}
                            />

                            {isLockedForecastMode ? (
                                <Line
                                    type="monotone"
                                    dataKey="forecast"
                                    stroke="var(--vb-line-strong)"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    connectNulls={false}
                                />
                            ) : null}
                        </AreaChart>
                    </ResponsiveContainer>

                </div>
                {trending ? (
                    <div className="mt-3 text-sm font-medium text-vb-text flex items-center gap-1">
                        {(() => {
                            const isPositive = trending.startsWith('+');
                            const isNegative = trending.startsWith('-');
                            const text = isPositive
                                ? `Trending up by ${trending} this month!`
                                : isNegative
                                    ? `Trending down by ${trending} this month`
                                    : null;

                            if (!text) return null;

                            return (
                                <>
                                    {text}
                                    {isPositive ? (
                                        <ArrowUpRight className="h-4 w-4 text-vb-pass" />
                                    ) : isNegative ? (
                                        <ArrowDownLeft className="h-4 w-4 text-vb-fail" />
                                    ) : null}
                                </>
                            );
                        })()}
                    </div>
                ) : null}
            </ProgressShell>
    );
};

export default ProgressTracker;
